import { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ingredientsApi } from '@/api/ingredients';
import { suppliersApi }   from '@/api/suppliers';
import { useToast }       from '@/providers/ToastProvider';
import { getErrorMessage } from '@/api/client';
import {
  PageHeader, Button, Input, Select, FormField, Card, CardHeader, CardTitle, CardContent, Spinner,
} from '@/components/ui';
import { ArrowLeft } from 'lucide-react';

const schema = z.object({
  name:         z.string().min(1, 'Required'),
  category_id:  z.string().optional(),
  supplier_id:  z.string().optional(),
  unit_size:    z.coerce.number().positive('Must be > 0'),
  unit_price:   z.coerce.number().min(0),
  vat_rate:     z.enum(['0', '0.04', '0.10', '0.21']).transform(Number),
  sugars:       z.coerce.number().min(0),
  sml:          z.coerce.number().min(0),
  fat:          z.coerce.number().min(0),
  solids_diff:  z.coerce.number().min(0),
  solids_total: z.coerce.number().min(0),
  pod:          z.coerce.number().min(0),
  pac:          z.coerce.number().min(0),
  is_active:    z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

export default function IngredientFormPage() {
  const { t }        = useTranslation();
  const { id }       = useParams<{ id?: string }>();
  const isEdit       = !!id;
  const navigate     = useNavigate();
  const { toast }    = useToast();
  const qc           = useQueryClient();

  const { data: ingredient, isLoading } = useQuery({
    queryKey: ['ingredient', id],
    queryFn:  () => ingredientsApi.get(id!),
    enabled:  isEdit,
  });

  const { data: categories } = useQuery({ queryKey: ['ingredient-categories'], queryFn: ingredientsApi.listCategories });
  const { data: suppliers  } = useQuery({ queryKey: ['suppliers-all'],         queryFn: suppliersApi.listAll });

  const {
    register, handleSubmit, reset, watch, setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const [sugars, sml, fat, solids_diff, unit_price, vat_rate, unit_size] = watch(['sugars', 'sml', 'fat', 'solids_diff', 'unit_price', 'vat_rate', 'unit_size']);
  const priceWithTax   = ((Number(unit_price) || 0) * (1 + (Number(vat_rate) || 0))).toFixed(2);
  const totalPrice     = ((Number(unit_size) || 0) * (Number(unit_price) || 0)).toFixed(2);
  const totalWithTax   = ((Number(unit_size) || 0) * (Number(unit_price) || 0) * (1 + (Number(vat_rate) || 0))).toFixed(2);

  useEffect(() => {
    const total = (Number(sugars) || 0) + (Number(sml) || 0) + (Number(fat) || 0) + (Number(solids_diff) || 0);
    setValue('solids_total', parseFloat(total.toFixed(2)));
  }, [sugars, sml, fat, solids_diff, setValue]);

  useEffect(() => {
    if (ingredient) {
      const r2 = (v: unknown) => { const n = Number(v); return isNaN(n) ? '0.00' : n.toFixed(2); };
      reset({
        name:         ingredient.name,
        category_id:  ingredient.category_id ?? '',
        supplier_id:  ingredient.supplier_id ?? '',
        unit_size:    r2(ingredient.unit_size),
        unit_price:   r2(ingredient.unit_price),
        vat_rate:     (['0', '0.04', '0.10', '0.21'].includes(String(Number(ingredient.vat_rate).toFixed(2))) ? String(Number(ingredient.vat_rate).toFixed(2)) : '0'),
        sugars:       r2(ingredient.sugars),
        sml:          r2(ingredient.sml),
        fat:          r2(ingredient.fat),
        solids_diff:  r2(ingredient.solids_diff),
        solids_total: r2(ingredient.solids_total),
        pod:          r2(ingredient.pod),
        pac:          r2(ingredient.pac),
        is_active:    ingredient.is_active,
      } as unknown as FormValues);
    }
  }, [ingredient, reset]);

  const saveMutation = useMutation({
    mutationFn: (data: FormValues) => {
      const payload = {
        ...data,
        category_id: data.category_id || null,
        supplier_id: data.supplier_id || null,
      };
      return isEdit
        ? ingredientsApi.update(id!, payload)
        : ingredientsApi.create(payload);
    },
    onSuccess: (saved) => {
      toast(t('ingredients.saved'));
      qc.invalidateQueries({ queryKey: ['ingredients'] });
      if (isEdit) {
        qc.invalidateQueries({ queryKey: ['ingredient', id] });
      } else {
        navigate(`/ingredients/${(saved as { id: string }).id}`);
      }
    },
    onError: (err) => toast(getErrorMessage(err), 'error'),
  });

  if (isEdit && isLoading) return <Spinner className="py-20" />;

  return (
    <div>
      <PageHeader title={isEdit ? t('ingredients.edit') : t('ingredients.new')}>
        <Button asChild variant="outline" size="sm">
          <Link to="/ingredients"><ArrowLeft size={15} /> {t('common.back')}</Link>
        </Button>
      </PageHeader>

      <form onSubmit={handleSubmit(d => saveMutation.mutate(d))} className="space-y-6 max-w-3xl">
        {/* Basic info */}
        <Card>
          <CardHeader><CardTitle>{t('common.name')}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={t('ingredients.name')} error={errors.name?.message} required className="sm:col-span-2">
              <Input {...register('name')} />
            </FormField>
            <FormField label={t('ingredients.category')}>
              <Select {...register('category_id')}
                placeholder="— Select category —"
                options={categories?.map(c => ({ value: c.id, label: c.name })) ?? []}
              />
            </FormField>
            <FormField label={t('ingredients.supplier')}>
              <Select {...register('supplier_id')}
                placeholder="— Select supplier —"
                options={suppliers?.map(s => ({ value: s.id, label: s.name })) ?? []}
              />
            </FormField>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader><CardTitle>{t('ingredients.pricing')}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:grid-cols-4">
            <FormField label={t('ingredients.unitSize')} error={errors.unit_size?.message} required>
              <Input type="text" inputMode="decimal" {...register('unit_size')} />
            </FormField>
            <FormField label={t('ingredients.unitPrice')} error={errors.unit_price?.message} required>
              <Input type="text" inputMode="decimal" {...register('unit_price')} />
            </FormField>
            <FormField label={t('ingredients.vatRate')} error={errors.vat_rate?.message} required>
              <Select {...register('vat_rate')}
                options={[
                  { value: '0',    label: '0%'  },
                  { value: '0.04', label: '4%'  },
                  { value: '0.10', label: '10%' },
                  { value: '0.21', label: '21%' },
                ]}
              />
            </FormField>
            <FormField label={t('ingredients.unitPriceWithTax')}>
              <Input type="text" readOnly value={priceWithTax} className="bg-muted cursor-default" />
            </FormField>
            <FormField label={t('ingredients.totalPrice')}>
              <Input type="text" readOnly value={totalPrice} className="bg-muted cursor-default" />
            </FormField>
            <FormField label={t('ingredients.totalPriceWithTax')}>
              <Input type="text" readOnly value={totalWithTax} className="bg-muted cursor-default" />
            </FormField>
          </CardContent>
        </Card>

        {/* Technical values */}
        <Card>
          <CardHeader>
            <CardTitle>{t('ingredients.technicalValues')}</CardTitle>
            <p className="text-xs text-muted-foreground">All values are expressed as g per 100g of ingredient (% by weight). POD and PAC are index values relative to sucrose = 100.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {(['sugars', 'sml', 'fat', 'solids_diff', 'pod', 'pac'] as const).map(field => (
              <FormField key={field} label={t(`ingredients.${field === 'solids_diff' ? 'solidsDiff' : field}`)} error={(errors as Record<string, {message?: string}>)[field]?.message}>
                <Input type="text" inputMode="decimal" {...register(field)} />
              </FormField>
            ))}
            <FormField label={t('ingredients.solidsTotal')}>
              <Input
                type="text"
                readOnly
                value={((Number(sugars)||0)+(Number(sml)||0)+(Number(fat)||0)+(Number(solids_diff)||0)).toFixed(2)}
                className="bg-muted cursor-default"
              />
            </FormField>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" loading={isSubmitting || saveMutation.isPending}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/ingredients')}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
